import React, { useCallback, useEffect, useState } from 'react';
import { CalendarOff, Paperclip } from 'lucide-react';
import { attendanceAPI } from '../../services/api';
import { useToast, useConfirm } from '../UIProvider';
import { authedMediaUrl } from '../../utils/media';
import { LEAVE_STATUS, datePart, formatDays } from './attendanceStatus';
import LeaveRequestDialog from './LeaveRequestDialog';
import { FEATURES } from '../../config/features';

// A person's own leave, under their attendance: what they have left, what they have asked for, and
// the way to ask for more. A pending request can be withdrawn; once it is decided it stands.

/** "2 Oct", or "2 Oct to 5 Oct", with "half day" when it is one. */
export const describeRange = (request) => {
  const from = datePart(request.fromDate, { day: 'numeric', month: 'short' });
  if (request.halfDay) return `${from}, half day`;
  if (request.fromDate === request.toDate) return from;
  return `${from} to ${datePart(request.toDate, { day: 'numeric', month: 'short' })}`;
};

const MyLeaveCard = () => {
  const notify = useToast();
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [asking, setAsking] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await attendanceAPI.myLeave();
      setData(res);
      setError(null);
    } catch (e) {
      setError(e?.message || 'Your leave could not be loaded.');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (request) => {
    const ok = await confirm({
      title: 'Withdraw this request?',
      message: `${request.typeName}, ${describeRange(request)}. It will not be sent for approval.`,
      confirmText: 'Withdraw',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await attendanceAPI.cancelLeave(request.id);
      notify('success', 'Request withdrawn');
      load();
    } catch (e) {
      notify('error', e?.message || 'The request could not be withdrawn.');
    }
  };

  const balances = data?.balances || [];
  const requests = data?.requests || [];

  return (
    <>
      <section aria-label="My leave" className="bg-white rounded-2xl shadow-xl p-5 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-semibold text-gray-900">My leave</h2>
          <button
            type="button"
            onClick={() => setAsking(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700"
          >
            <CalendarOff className="w-4 h-4" aria-hidden="true" /> Request leave
          </button>
        </div>

        {error ? (
          <p className="text-sm text-red-700">{error}</p>
        ) : (
          <>
            {FEATURES.leaveAllowances && balances.length > 0 && (
              <ul className="flex flex-wrap gap-2">
                {balances.map((b) => (
                  <li key={b.typeId} className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-1.5 text-sm">
                    <span className="text-gray-900 font-medium">{b.typeName}</span>{' '}
                    <span className="text-gray-600">
                      {b.quota == null ? 'no limit' : `${b.remaining} of ${b.quota} left`}
                      {b.pending > 0 ? `, ${formatDays(b.pending)} pending` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {requests.length === 0 ? (
              <p className="text-sm text-gray-500">You have not asked for any leave this year.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {requests.map((r) => {
                  const chip = LEAVE_STATUS[r.status] || LEAVE_STATUS.PENDING;
                  return (
                    <li key={r.id} className="py-2.5 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {r.typeName}
                          {r.paid === false && <span className="text-gray-500 font-normal"> (unpaid)</span>}
                          <span className="text-gray-500 font-normal"> · {describeRange(r)} · {formatDays(r.days)}</span>
                        </p>
                        <p className="text-xs text-gray-600 truncate">{r.reason}</p>
                        {r.decisionNote && <p className="text-xs text-gray-600">“{r.decisionNote}” — {r.decidedByName}</p>}
                        {r.attachmentUrl && (
                          <a href={authedMediaUrl(r.attachmentUrl)} target="_blank" rel="noreferrer" className="text-xs text-navy-700 hover:underline inline-flex items-center gap-1 mt-0.5">
                            <Paperclip className="w-3 h-3" aria-hidden="true" /> Attachment
                          </a>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${chip.chip}`}>{chip.label}</span>
                        {r.status === 'PENDING' && (
                          <button type="button" onClick={() => cancel(r)} className="text-xs text-red-700 hover:underline">Withdraw</button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </section>

      {asking && (
        <LeaveRequestDialog
          onClose={() => setAsking(false)}
          onSaved={() => { setAsking(false); load(); }}
        />
      )}
    </>
  );
};

export default MyLeaveCard;
